import { useState, useEffect, useRef } from "react";
import { Outlet, useNavigate } from "react-router-dom";

import useMediaQuery from "@mui/material/useMediaQuery";
import { Box, Container, Toolbar, Chip } from "@mui/material/";

// project import
import Drawer from "./Drawer";
import Header from "./Header";
import Footer from "./Footer";
import HorizontalBar from "./Drawer/HorizontalBar";
import Loader from "components/Loader";
import Breadcrumbs from "components/@extended/Breadcrumbs";
import AuthGuard from "utils/route-guard/AuthGuard";

import { MenuOrientation } from "config";
import useConfig from "hooks/useConfig";
import { useGetMenuMaster } from "api/menu";
import useDataSourceManager from "hooks/useDataSourceManager";
import useZustandStore from "zustandStore";
// ws
import { useWebSocket } from "contexts/WebSocketContext";
import { useBreadStorageContext } from "contexts/BreadStorageContext";
import { useLocation } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import cloneDeep from "lodash.clonedeep";

import { sendWsDebugMessage } from "utils/wsDebugger";

// controller
import controller from "./controller";
// fake data
import {
  defaultEnterprise,
  defaultStandaloneOrg,
  defaultManageOrg,
  defaultSite,
} from "config/bogus/manageScope/const.js";

const CHECK_MOCKUP = process.env.REACT_APP_IS_MOCKUP;
const CHECK_CONTEXT = process.env.REACT_APP_RUNNING_CONTEXT;
// ==============================|| MAIN LAYOUT ||============================== //

export default function DashboardLayout() {
  const ws = useWebSocket();
  const [socket, setSocket] = useState(null);
  const [isWebSocketInitialized, setIsWebSocketInitialized] = useState(false);
  const [wsData, setWsData] = useState([]);
  const [fetchSystemFlag, setFetchSystemFlag] = useState(true);

  const navigate = useNavigate();
  const location = useLocation();
  const url = location.pathname;
  const level = location.pathname.split("/")[1];
  const router = location.pathname.split("/")[2];
  const blankKey = [
    "account/profile",
    "realm",
    "user_controller_invitation",
    "user_controller_expired",
    "user_controller_error",
    "invitation",
  ];

  const { bread } = useBreadStorageContext();
  const [scopeData, setScopeData] = useState({
    realm: [],
    orgs: [],
    site: [],
  });
  const [scopeReady, setScopeReady] = useState(false);

  let defaultSpread = {
    callRealmData: false,
    checkSelectRealm: false,
    callOrgData: false,
    checkSelectOrg: false,
    callSiteData: false,
    checkSelectSite: false,
  };
  const [spreadProps, setSpreadProps] = useState(cloneDeep(defaultSpread));

  const { dataMap, getData, decodeTokenData, sendRequest, sendBatch } =
    useDataSourceManager(wsData);

  // Easter Egg Trigger for WebSocket Debugger
  useEffect(() => {
    let unlockMode = false;
    let typedSequence = '';
    const secretKey = 'whywebsocket';

    const handleKeyDown = (e) => {
      // 1. Check for unlock hotkey (Cmd + Shift + D or Ctrl + Shift + D)
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === 'd') {
        e.preventDefault();
        unlockMode = true;
        typedSequence = '';
        console.log('Debugger unlock mode activated.');
        return;
      }

      // 2. Check for sequence if unlocked
      if (unlockMode) {
        // Ignore modifiers
        if (e.key.length === 1) {
          typedSequence += e.key.toLowerCase();

          if (secretKey.startsWith(typedSequence)) {
            if (typedSequence === secretKey) {
              window.open('/ws-debugger', '_blank');
              unlockMode = false;
              typedSequence = '';
            }
          } else {
            typedSequence = e.key.toLowerCase() === secretKey[0] ? secretKey[0] : '';
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    if (ws) {
      console.log("Layout WebSocket connection");
      setIsWebSocketInitialized(true); // WebSocket 已經初始化完畢

      ws.onmessage = (message) => {
        const data = JSON.parse(message.data);
        sendWsDebugMessage('response', data);
        // console.log(data);
        const resource = data.resource;
        // console.log("======== ws.onmessage ========");
        // console.log(resource);

        switch (true) {
          // case matchResource("^/users(/\\d*)?$"):
          //   console.log("User data:", data);
          //   break;
          default:
            // console.log("data:", data);
            let tmpWSdata = {
              // update: true,
              uuid: data.id,
              response: data.result,
              error: data.error,
            };
            setWsData((prevQueue) => [...prevQueue, tmpWSdata]);
            break;
        }
        // no token, return to Login.
        if (data.error && data.error.code === 401) {
          localStorage.removeItem("token");
          localStorage.removeItem("refreshToken");
          window.location.reload();
        }
      };

      ws.onclose = () => {
        console.log("WebSocket connection closed");
      };

      setSocket(ws);
    } else {
      if (CHECK_MOCKUP === "true") {
        setIsWebSocketInitialized(true);
        setSocket(true);
      }
    }
  }, [ws]);

  const { menuMasterLoading } = useGetMenuMaster();
  const downLG = useMediaQuery((theme) => theme.breakpoints.down("lg"));
  const { container, menuOrientation } = useConfig();
  const isHorizontal =
    menuOrientation === MenuOrientation.HORIZONTAL && !downLG;

  const { setIsNavDisabled, setServiceStatus, setStatsData } =
    useZustandStore();

  const executeInitializationSequence = async (
    tmpSpread,
    level,
    emptyBreadcrumbs,
  ) => {
    console.log(">>> [InitSeq] START", { level, tmpSpread });
    if (level === "site") setIsNavDisabled(true);
    setScopeReady(false);
    setSpreadProps(tmpSpread);

    try {
      // 1. Memberships
      const membershipUuid = uuidv4();
      console.log(">>> [InitSeq] Fetching Memberships...");
      const membershipRes = await sendBatch([
        {
          apiKey: "/me/memberships.read",
          requestUuid: membershipUuid,
        },
      ]);

      const membershipData = membershipRes.sendBatchDataMap[membershipUuid];
      console.log(">>> [InitSeq] Memberships Result:", membershipData ? "Success" : "Empty/Failed");

      if (membershipData?.code !== undefined) {
        localStorage.setItem(
          "selectBreadcrumbs",
          JSON.stringify(emptyBreadcrumbs),
        );
        navigate("/");
        return;
      }

      if (!membershipData) return;

      let currentBreadData = cloneDeep(scopeData);
      let tmpSelectedBread = JSON.parse(
        localStorage.getItem("selectBreadcrumbs") || "{}",
      );
      console.log(">>> [InitSeq] Initial Breadcrumbs from storage:", tmpSelectedBread);

      if (CHECK_CONTEXT === "cloud") {
        let tmpEntData = { data: [] };
        let tmpAloneOrgData = { data: [] };
        if (
          membershipData.data.enterprises &&
          membershipData.data.enterprises.length > 0
        ) {
          tmpEntData.data = membershipData.data.enterprises;
        }
        if (
          membershipData.data.standaloneOrgs &&
          membershipData.data.standaloneOrgs.length > 0
        ) {
          tmpAloneOrgData.data = membershipData.data.standaloneOrgs;
        }
        const result = controller.mergeRealmData(tmpEntData, tmpAloneOrgData);
        currentBreadData.realm = result;

        if (tmpSpread.checkSelectRealm) {
          const findItem = currentBreadData.realm.find(
            (item) => item.id === tmpSelectedBread.realm.id,
          );
          if (!findItem) {
            console.log(">>> [InitSeq] Realm not found, redirecting to /realms");
            localStorage.setItem(
              "selectBreadcrumbs",
              JSON.stringify(emptyBreadcrumbs),
            );
            navigate("/realms");
            return;
          } else {
            // check role & name
            if (findItem.roleId !== tmpSelectedBread.realm.roleId) {
              console.log(">>> [InitSeq] Realm role mismatch, redirecting to /realms");
              localStorage.setItem(
                "selectBreadcrumbs",
                JSON.stringify(emptyBreadcrumbs),
              );
              navigate("/realms");
              return;
            } else if (findItem.name !== tmpSelectedBread.realm.name) {
              console.log(">>> [InitSeq] Realm name changed, updating breadcrumbs");
              tmpSelectedBread.realm = findItem;
              tmpSelectedBread.orgs = {};
              tmpSelectedBread.site = {};
              localStorage.setItem(
                "selectBreadcrumbs",
                JSON.stringify(tmpSelectedBread),
              );
            }
          }
        } else {
          localStorage.setItem(
            "selectBreadcrumbs",
            JSON.stringify(emptyBreadcrumbs),
          );
        }
      } else {
        // Premises context
        console.log(">>> [InitSeq] Context: Premises");
        let tmpEntData = { data: [] };
        let tmpAloneOrgData = { data: [] };
        if (
          membershipData.data.standaloneOrgs &&
          membershipData.data.standaloneOrgs.length > 0
        ) {
          tmpAloneOrgData.data.push(membershipData.data.standaloneOrgs[0]);
        }
        const result = controller.mergeRealmData(tmpEntData, tmpAloneOrgData);
        if (result.length !== 0) {
          currentBreadData.realm = result;
          if (!tmpSpread.checkSelectRealm) {
            tmpSelectedBread = {
              realm: currentBreadData.realm[0],
              orgs: {},
              site: {},
            };
          } else {
            tmpSelectedBread.realm = currentBreadData.realm[0];
            tmpSelectedBread.orgs = {};
          }
          console.log(">>> [InitSeq] Premises Breadcrumbs updated:", tmpSelectedBread);
          localStorage.setItem(
            "selectBreadcrumbs",
            JSON.stringify(tmpSelectedBread),
          );
        } else {
          localStorage.setItem(
            "selectBreadcrumbs",
            JSON.stringify(emptyBreadcrumbs),
          );
          return;
        }
      }

      setScopeData(currentBreadData);

      // 2. Organizations
      if (tmpSpread.callOrgData) {
        console.log(">>> [InitSeq] Step 2: Fetching Organizations...");
        const orgUuid = uuidv4();
        const orgRes = await sendBatch([
          {
            apiKey: "/ents/:entId/orgs.read",
            requestUuid: orgUuid,
            restfulPlaceholders: { entId: tmpSelectedBread.realm.id },
          },
        ]);

        const orgData = orgRes.sendBatchDataMap[orgUuid];
        if (orgData?.code !== undefined) {
          localStorage.setItem(
            "selectBreadcrumbs",
            JSON.stringify(emptyBreadcrumbs),
          );
          navigate("/");
          return;
        }

        const orgResult = controller.mergeOrgData(orgData);
        currentBreadData.orgs = orgResult;

        if (tmpSpread.checkSelectOrg) {
          const findItem = currentBreadData.orgs.find(
            (item) => item.id === tmpSelectedBread.orgs.id,
          );
          if (!findItem) {
            tmpSelectedBread.orgs = {};
            tmpSelectedBread.site = {};
            localStorage.setItem(
              "selectBreadcrumbs",
              JSON.stringify(tmpSelectedBread),
            );
            navigate("/msp/organizations");
            return;
          } else if (findItem.roleId !== tmpSelectedBread.orgs.roleId) {
            tmpSelectedBread.orgs = {};
            tmpSelectedBread.site = {};
            localStorage.setItem(
              "selectBreadcrumbs",
              JSON.stringify(tmpSelectedBread),
            );
            navigate("/msp/organizations");
            return;
          } else if (findItem.name !== tmpSelectedBread.orgs.name) {
            tmpSelectedBread.orgs = findItem;
            tmpSelectedBread.site = {};
            localStorage.setItem(
              "selectBreadcrumbs",
              JSON.stringify(tmpSelectedBread),
            );
          }
        } else {
          tmpSelectedBread.orgs = {};
          tmpSelectedBread.site = {};
          localStorage.setItem(
            "selectBreadcrumbs",
            JSON.stringify(tmpSelectedBread),
          );
        }
        setScopeData(currentBreadData);
      }

      // 3. Sites
      if (tmpSpread.callSiteData) {
        console.log(">>> [InitSeq] Step 3: Fetching Sites...");
        const siteUuid = uuidv4();
        const standalone =
          !tmpSelectedBread.orgs.id ||
          Object.keys(tmpSelectedBread.orgs).length === 0;

        const siteApiKey = standalone
          ? "/orgs/:orgId/sites.read"
          : "/ents/:entId/orgs/:orgId/sites.read";
        const siteRestfulParams = standalone
          ? { orgId: tmpSelectedBread.realm.id }
          : {
              entId: tmpSelectedBread.realm.id,
              orgId: tmpSelectedBread.orgs.id,
            };

        const siteRes = await sendBatch([
          {
            apiKey: siteApiKey,
            requestUuid: siteUuid,
            restfulPlaceholders: siteRestfulParams,
          },
        ]);

        const siteData = siteRes.sendBatchDataMap[siteUuid];
        if (siteData?.code !== undefined) {
          if (tmpSelectedBread.realm.hasManageOrg === true) {
            tmpSelectedBread.orgs = {};
            tmpSelectedBread.site = {};
            localStorage.setItem(
              "selectBreadcrumbs",
              JSON.stringify(tmpSelectedBread),
            );
            navigate("/msp/organizations");
          } else {
            localStorage.setItem(
              "selectBreadcrumbs",
              JSON.stringify(emptyBreadcrumbs),
            );
            navigate("/");
          }
          return;
        }

        const siteResult = controller.mergeSiteData(siteData);
        currentBreadData.site = siteResult;

        if (tmpSpread.checkSelectSite) {
          const findItem = currentBreadData.site.find(
            (item) => item.id === tmpSelectedBread.site.id,
          );
          if (!findItem) {
            tmpSelectedBread.site = {};
            localStorage.setItem(
              "selectBreadcrumbs",
              JSON.stringify(tmpSelectedBread),
            );
            navigate("/org/sites");
            return;
          } else if (findItem.roleId !== tmpSelectedBread.site.roleId) {
            tmpSelectedBread.site = {};
            localStorage.setItem(
              "selectBreadcrumbs",
              JSON.stringify(tmpSelectedBread),
            );
            navigate("/org/sites");
            return;
          } else if (findItem.name !== tmpSelectedBread.site.name) {
            tmpSelectedBread.site = findItem;
            localStorage.setItem(
              "selectBreadcrumbs",
              JSON.stringify(tmpSelectedBread),
            );
          }
        } else {
          tmpSelectedBread.site = {};
          localStorage.setItem(
            "selectBreadcrumbs",
            JSON.stringify(tmpSelectedBread),
          );
        }
        setScopeData(currentBreadData);
      }

      // 4. Service Status
      if (level === "site") {
        console.log(">>> [InitSeq] Step 4: Fetching Service Status...");
        const serviceStatusUuid = uuidv4();
        const standalone =
          !tmpSelectedBread.orgs.id ||
          Object.keys(tmpSelectedBread.orgs).length === 0;
        const apiServiceStatusUrl = `${standalone ? "" : "/ents/:entId"}/orgs/:orgId/sites/:siteId/license/service/status.read`;
        const restfulParams = standalone
          ? {
              orgId: tmpSelectedBread.realm.id,
              siteId: tmpSelectedBread.site.id,
            }
          : {
              entId: tmpSelectedBread.realm.id,
              orgId: tmpSelectedBread.orgs.id,
              siteId: tmpSelectedBread.site.id,
            };

        const isUnityRd3 = window.location.hostname.includes('unity-rd3');
        let serviceStatusData = null;

        if (isUnityRd3) {
          const serviceStatusRes = await sendBatch([
            {
              apiKey: apiServiceStatusUrl,
              requestUuid: serviceStatusUuid,
              restfulPlaceholders: restfulParams,
            },
          ]);

          serviceStatusData =
            serviceStatusRes.sendBatchDataMap[serviceStatusUuid];
        } else {
          serviceStatusData = {
            data: {
              services: {
                healthAI: true,
                nmsPro: true,
              },
            },
          };
        }
        if (serviceStatusData?.data) {
          setServiceStatus(serviceStatusData.data);

          // 5. Stats
          console.log(">>> [InitSeq] Step 5: Fetching Stats...");
          const statsUuid = uuidv4();
          const apiStatsUrl = `${standalone ? "" : "/ents/:entId"}/orgs/:orgId/sites/:siteId/stats.read`;
          const statsRequestParams = {
            data: {
              networkStatus: {},
              cpuTop5: {},
              memTop5: {},
              poeTop5: {},
              pocConsumption: {},
              top5TrafficApSsid: {},
              top5TrafficSwitch: {},
              top5TrafficAp: {},
              apChannelUtilization: {},
              wirelessTrafficUsage: {},
              accessPointsOverview: {},
              wirelessClientsRssi: {},
              wirelessClientsBand: {},
              top5TrafficWirelessClients: {},
              top5WirelessClientsAp: {},
              wirelessClientsActivities: {},
            },
          };

          const statsRes = await sendBatch([
            {
              apiKey: apiStatsUrl,
              requestUuid: statsUuid,
              restfulPlaceholders: restfulParams,
              params: statsRequestParams,
            },
          ]);

          const statsData = statsRes.sendBatchDataMap[statsUuid];
          if (statsData?.data) {
            setStatsData(statsData.data);
          }
        }
      }

      console.log(">>> [InitSeq] FINISHED successfully");
      setScopeReady(true);
      setIsNavDisabled(false);
    } catch (error) {
      console.error(">>> [InitSeq] FATAL ERROR:", error);
      setIsNavDisabled(false);
      setScopeReady(true);
    }
  };

  const executeMockupInitialization = async (level, emptyBreadcrumbs) => {
    if (level === "site") setIsNavDisabled(true);
    setScopeReady(false);

    let tmpBreadData = cloneDeep(scopeData);
    let tmpEnt = {};
    let tmpOrg = {};

    if (CHECK_CONTEXT !== "cloud") {
      let nullEnt = { data: [] };
      tmpEnt = controller.mergeRealmData(nullEnt, defaultStandaloneOrg);
    } else {
      tmpEnt = controller.mergeRealmData(
        defaultEnterprise,
        defaultStandaloneOrg,
      );
      tmpOrg = controller.mergeOrgData(defaultManageOrg);
    }

    const tmpSite = controller.mergeSiteData(defaultSite);
    tmpBreadData.realm = tmpEnt;
    tmpBreadData.orgs = tmpOrg;
    tmpBreadData.site = tmpSite;
    setScopeData(tmpBreadData);

    let tmpSelectedBread = JSON.parse(
      localStorage.getItem("selectBreadcrumbs") || "{}",
    );

    if (Object.keys(tmpSelectedBread).length === 0) {
      localStorage.setItem(
        "selectBreadcrumbs",
        JSON.stringify(emptyBreadcrumbs),
      );
      navigate("/");
      return;
    }

    // Mockup redirect logic
    if (CHECK_CONTEXT === "cloud") {
      if (level === "msp") {
        if (Object.keys(tmpSelectedBread.realm).length === 0)
          navigate("/realms");
        else {
          tmpSelectedBread.orgs = {};
          tmpSelectedBread.site = {};
          localStorage.setItem(
            "selectBreadcrumbs",
            JSON.stringify(tmpSelectedBread),
          );
        }
      } else if (level === "org") {
        if (Object.keys(tmpSelectedBread.realm).length === 0)
          navigate("/realms");
        else if (
          tmpSelectedBread.realm.hasManageOrg &&
          Object.keys(tmpSelectedBread.orgs).length === 0
        )
          navigate("/msp/organizations");
        else {
          tmpSelectedBread.site = {};
          localStorage.setItem(
            "selectBreadcrumbs",
            JSON.stringify(tmpSelectedBread),
          );
        }
      } else if (level === "site") {
        if (Object.keys(tmpSelectedBread.realm).length === 0)
          navigate("/realms");
        else if (Object.keys(tmpSelectedBread.site).length === 0)
          navigate("/org/sites");
        else if (
          tmpSelectedBread.realm.hasManageOrg &&
          Object.keys(tmpSelectedBread.orgs).length === 0
        )
          navigate("/org/sites");
      }
    } else {
      // Premises mockup redirect
      if (level === "realms" || level === "msp") navigate("/org/sites");
      else if (level === "org") {
        tmpSelectedBread = { realm: tmpBreadData.realm[0], orgs: {}, site: {} };
        localStorage.setItem(
          "selectBreadcrumbs",
          JSON.stringify(tmpSelectedBread),
        );
      } else if (level === "site") {
        if (Object.keys(tmpSelectedBread.realm).length === 0)
          navigate("/org/sites");
      }
    }

    if (level === "site") {
      const serviceStatusUuid = uuidv4();
      const standalone =
        !tmpSelectedBread.orgs.id ||
        Object.keys(tmpSelectedBread.orgs).length === 0;
      const apiServiceStatusUrl = `${standalone ? "" : "/ents/:entId"}/orgs/:orgId/sites/:siteId/license/service/status.read`;
      const restfulParams = standalone
        ? { orgId: tmpSelectedBread.realm.id, siteId: tmpSelectedBread.site.id }
        : {
            entId: tmpSelectedBread.realm.id,
            orgId: tmpSelectedBread.orgs.id,
            siteId: tmpSelectedBread.site.id,
          };

      const isUnityRd3 = window.location.hostname.includes('unity-rd3');
      let serviceStatusData = null;

      if (isUnityRd3) {
        const serviceStatusRes = await sendBatch([
          {
            apiKey: apiServiceStatusUrl,
            requestUuid: serviceStatusUuid,
            restfulPlaceholders: restfulParams,
          },
        ]);
        serviceStatusData =
          serviceStatusRes.sendBatchDataMap[serviceStatusUuid];
      } else {
        serviceStatusData = {
          data: {
            services: {
              healthAI: true,
              nmsPro: true,
            },
          },
        };
      }
      if (serviceStatusData?.data) {
        setServiceStatus(serviceStatusData.data);
      }
    }

    setScopeReady(true);
    setIsNavDisabled(false);
  };

  useEffect(() => {
    if (fetchSystemFlag === false) {
      let emptyBreadcrumbs = { realm: {}, orgs: {}, site: {} };
      if (isWebSocketInitialized === true && CHECK_MOCKUP !== "true") {
        let tmpSelectedBread = JSON.parse(
          localStorage.getItem("selectBreadcrumbs") || "{}",
        );
        if (Object.keys(tmpSelectedBread).length === 0) {
          tmpSelectedBread = cloneDeep(emptyBreadcrumbs);
        }
        let tmpSpread = cloneDeep(defaultSpread);
        tmpSpread.callRealmData = true;

        if (level === "realms") {
          localStorage.setItem(
            "selectBreadcrumbs",
            JSON.stringify(emptyBreadcrumbs),
          );
          if (CHECK_CONTEXT === "premises") {
            navigate("/org/sites");
          }
        } else {
          if (
            tmpSelectedBread.realm &&
            Object.keys(tmpSelectedBread.realm).length !== 0
          ) {
            if (tmpSelectedBread.realm.hasManageOrg === true) {
              if (level === "msp") {
                tmpSpread.checkSelectRealm = true;
                tmpSpread.callOrgData = true;
              } else if (level === "org") {
                if (Object.keys(tmpSelectedBread.orgs).length === 0) {
                  navigate("/msp/organizations");
                } else {
                  tmpSpread.checkSelectRealm = true;
                  tmpSpread.callOrgData = true;
                  tmpSpread.checkSelectOrg = true;
                  tmpSpread.callSiteData = true;
                }
              } else if (level === "site") {
                if (Object.keys(tmpSelectedBread.site).length === 0) {
                  navigate("/org/sites");
                } else {
                  tmpSpread.checkSelectRealm = true;
                  tmpSpread.callOrgData = true;
                  tmpSpread.checkSelectOrg = true;
                  tmpSpread.callSiteData = true;
                  tmpSpread.checkSelectSite = true;
                }
              } else {
                tmpSpread.checkSelectRealm = true;
                if (Object.keys(tmpSelectedBread.orgs).length !== 0) {
                  tmpSpread.callOrgData = true;
                  tmpSpread.checkSelectOrg = true;
                }
                if (Object.keys(tmpSelectedBread.site).length !== 0) {
                  tmpSpread.callSiteData = true;
                  tmpSpread.checkSelectSite = true;
                }
              }
            } else {
              if (level === "msp") {
                navigate("/org/sites");
              } else if (level === "org") {
                tmpSpread.checkSelectRealm = true;
                tmpSpread.callSiteData = true;
              } else if (level === "site") {
                if (Object.keys(tmpSelectedBread.site).length === 0) {
                  navigate("/org/sites");
                } else {
                  tmpSpread.checkSelectRealm = true;
                  tmpSpread.callSiteData = true;
                  tmpSpread.checkSelectSite = true;
                }
              } else {
                tmpSpread.checkSelectRealm = true;
                if (Object.keys(tmpSelectedBread.site).length !== 0) {
                  tmpSpread.callSiteData = true;
                  tmpSpread.checkSelectSite = true;
                }
              }
            }
          } else {
            localStorage.setItem(
              "selectBreadcrumbs",
              JSON.stringify(emptyBreadcrumbs),
            );
            if (level === "msp" || level === "org" || level === "site") {
              if (CHECK_CONTEXT === "cloud") {
                navigate("/realms");
              } else {
                navigate("/org/sites");
              }
            }
          }
        }
        executeInitializationSequence(tmpSpread, level, emptyBreadcrumbs);
      } else if (isWebSocketInitialized === true && CHECK_MOCKUP === "true") {
        executeMockupInitialization(level, emptyBreadcrumbs);
      }
    }
  }, [url, isWebSocketInitialized, bread, fetchSystemFlag]);

  if (menuMasterLoading) return <Loader />;

  return (
    <>
      {isWebSocketInitialized && socket && (
        <AuthGuard
          fetchSystemFlag={fetchSystemFlag}
          setFetchSystemFlag={setFetchSystemFlag}
        >
          {/* {!spreadInfo && <Loader />} */}
          {/* {spreadInfo && }*/}
          <Box sx={{ display: "flex", width: "100%" }}>
            <Header />
            {blankKey.some((keyword) => url.includes(keyword)) ? (
              ""
            ) : !isHorizontal ? (
              <Drawer />
            ) : (
              <HorizontalBar />
            )}
            <Box
              component='main'
              sx={{
                backgroundColor: "primary.background",
                width: "calc(100% - 260px)",
                flexGrow: 1,
                p: { xs: 2, sm: 3 },
                minHeight: "100vh",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {blankKey.some((keyword) => url.includes(keyword)) ? null : (
                <Toolbar sx={{ mt: isHorizontal ? 8 : "inherit" }} />
              )}
              <Container
                maxWidth={false}
                sx={{
                  ...(container && { px: { xs: 0, sm: 2 } }),
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                  minWidth: { xs: 0, md: "789px" },

                  flex: 1,
                  minHeight: 0,
                }}
              >
                {blankKey.some((keyword) => url.includes(keyword)) ? null : (
                  <Box sx={{display: "flex"}}>
                    <Breadcrumbs
                      wsData={wsData}
                      setWsData={setWsData}
                      scopeData={scopeData}
                      setScopeData={setScopeData}
                    />
                    {
                      router === "AInsight" &&
                      <Chip
                        label="PRE-VIEW"
                        size="small"
                        sx={{
                          backgroundColor: "#FFBC31",
                          color: "#fff",
                          fontWeight: 700,
                          fontSize: 12,
                          borderRadius: 5,
                          height: 24,
                          px: 0.7,
                          ml: 1.5,
                          mt: 0.8,
                          "& .MuiChip-label": {
                            lineHeight: 1,
                            paddingTop: "1px",
                          },
                        }}
                      />
                    }
                  </Box>
                )}
                <Box sx={{ flex: 1, minHeight: 0 }}>
                  <Outlet
                    context={{
                      wsData,
                      setWsData,
                      scopeData,
                      setScopeData,
                      scopeReady,
                    }}
                  />
                </Box>
                <Footer />
              </Container>
            </Box>
          </Box>
        </AuthGuard>
      )}
    </>
  );
}
